/**
 * Self-service coach onboarding (mobile-first, account-billing model).
 *
 * Commercial access belongs to the USER ACCOUNT, so activation/checkout happens
 * BEFORE (and independently of) any team. Locked product hierarchy:
 *   account → activate Trainnova Coach → use app → (optionally) create a team
 *
 * Entry for a zero-team user:
 *   no access  → Coach OFFER directly (Stripe) → return → welcome → create team → success
 *   has access → handled upstream (AuthContext routes to /oversikt); if landed
 *                here (e.g. activated=1) → welcome → create team → success
 * Invited players/parents use the secondary "Join a team" action (never billing-gated).
 *
 * has_access is resolved by the backend central resolver via useAccountAccess.
 * We never re-implement status rules here.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useAccountAccess } from '../hooks/useAccountAccess';
import Onboarding from '../components/Onboarding';
import { provisionSelfServiceTeam, createAccountCheckout, switchTeam } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  CalendarPlus,
  CalendarRange,
  Check,
  CheckCircle2,
  CreditCard,
  Loader2,
  ShieldCheck,
  Sparkles,
  Users,
  UsersRound,
} from 'lucide-react';
import LoginLanguageSwitcher from '../components/auth/LoginLanguageSwitcher';

export const PENDING_TEAM_KEY = 'trainova.onboarding.pendingTeamId';
const IDEM_KEY = 'trainova.onboarding.idemKey';

const SURFACE_CLASS =
  "overflow-hidden rounded-[24px] border border-[rgb(var(--tn-border-ffffff))]/[0.08] bg-[rgb(var(--tn-bg-11141b))]/94 " +
  "shadow-[0_30px_90px_-28px_rgba(0,0,0,0.88)] ring-1 ring-inset ring-[rgb(var(--tn-border-ffffff))]/[0.035] backdrop-blur-xl";

const PRIMARY_BUTTON_CLASS =
  'h-12 rounded-xl bg-[#C6A85E] text-[15px] font-semibold text-[#171109] ' +
  'shadow-[0_14px_34px_-14px_rgba(198,168,94,0.58)] transition-all ' +
  'hover:bg-[#D4B86A] active:scale-[0.99]';

const INPUT_CLASS =
  "h-12 rounded-xl border-[rgb(var(--tn-border-ffffff))]/10 bg-[rgb(var(--tn-bg-0b0e13))]/90 text-[15px] text-[rgb(var(--tn-text-ffffff))] " +
  "placeholder:text-[rgb(var(--tn-text-525252))] focus-visible:border-[#C6A85E]/55 focus-visible:ring-2 " +
  'focus-visible:ring-[#C6A85E]/20';

function makeIdemKey() {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch {
    // noop
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function Stepper({ mode }) {
  const { t } = useTranslation(['self_onboarding']);

  const activeStep = mode === 'success' ? 3 : mode === 'welcome' || mode === 'create' ? 2 : 1;
  const steps = [
    t('self_onboarding:flow.step_activate'),
    t('self_onboarding:flow.step_team'),
    t('self_onboarding:flow.step_ready'),
  ];

  return (
    <div className="mx-auto w-full max-w-xl px-1" aria-label={t('self_onboarding:flow.progress_label')}>
      <div className="grid grid-cols-3 gap-2">
        {steps.map((label, index) => {
          const step = index + 1;
          const done = step < activeStep;
          const active = step === activeStep;

          return (
            <div key={label} className="min-w-0">
              <div className="mb-2 flex items-center">
                <div
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold transition-colors ${
                    done
                      ? 'border-[#C6A85E] bg-[#C6A85E] text-[#171109]'
                      : active
                        ? "border-[#C6A85E]/55 bg-[#C6A85E]/10 text-[rgb(var(--tn-text-c6a85e))]"
                        : "border-[rgb(var(--tn-border-ffffff))]/10 bg-[rgb(var(--tn-tint-ffffff))]/[0.025] text-[rgb(var(--tn-text-525252))]"
                  }`}
                >
                  {done ? <Check className="h-3.5 w-3.5" /> : step}
                </div>

                {step < steps.length && (
                  <div
                    className={`ml-2 h-px flex-1 ${
                      done ? 'bg-[#C6A85E]/60' : "bg-[rgb(var(--tn-tint-ffffff))]/[0.08]"
                    }`}
                  />
                )}
              </div>

              <p
                className={`truncate text-[10px] font-semibold uppercase tracking-[0.12em] sm:text-[11px] ${
                  active || done ? "text-[rgb(var(--tn-text-d4d4d4))]" : "text-[rgb(var(--tn-text-525252))]"
                }`}
              >
                {label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OnboardingShell({ mode, children }) {
  const { t } = useTranslation(['self_onboarding', 'common']);

  return (
    <div
      className="relative min-h-[100dvh] overflow-x-hidden bg-[rgb(var(--tn-bg-080a0e))] text-[rgb(var(--tn-text-ffffff))]"
      style={{
        background:
          "radial-gradient(900px 560px at 82% 8%, rgb(var(--tn-bg-2f3d5d) / 0.28), transparent 64%)," +
          'radial-gradient(760px 520px at 10% 90%, rgba(198,168,94,0.06), transparent 64%),' +
          "rgb(var(--tn-bg-080a0e))",
      }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)',
          backgroundSize: '58px 58px',
          maskImage: 'radial-gradient(80% 70% at 50% 38%, black, transparent)',
        }}
      />

      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-[440px] -top-[440px] h-[860px] w-[860px] rounded-full border border-[#C6A85E]/15"
      />

      <div className="relative mx-auto flex min-h-[100dvh] w-full max-w-6xl flex-col px-4 py-5 sm:px-6 sm:py-7">
        <header className="flex items-center justify-between gap-4">
          <div className="inline-flex items-center gap-2.5">
            <img
              src="/trainnova-logo.png"
              alt={t('common:brand.name')}
              className="h-8 w-auto"
            />
            <span className="text-lg font-semibold tracking-tight text-[rgb(var(--tn-text-ffffff))]">Trainnova</span>
          </div>

          <LoginLanguageSwitcher />
        </header>

        <main className="flex flex-1 flex-col justify-center py-7 sm:py-10">
          {mode !== 'loading' && (
            <div className="mb-7 sm:mb-8">
              <Stepper mode={mode} />
            </div>
          )}

          <div className="mx-auto w-full max-w-xl">
            {children}
          </div>
        </main>

        <footer className="pb-1 text-center text-[11px] text-neutral-700">
          © {new Date().getFullYear()} Trainnova
        </footer>
      </div>
    </div>
  );
}

function FieldIcon({ children }) {
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[rgb(var(--tn-border-ffffff))]/[0.07] bg-[rgb(var(--tn-tint-ffffff))]/[0.025] text-[rgb(var(--tn-text-a3a3a3))]">
      {children}
    </span>
  );
}

export default function SelfServiceOnboarding() {
  const { t } = useTranslation(['self_onboarding', 'common']);
  const { completeOnboarding, refreshUser } = useAuth();
  const access = useAccountAccess();
  const [searchParams] = useSearchParams();

  const [mode, setMode] = useState('loading'); // loading | join | offer | welcome | create | success
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ club: '', team: '', age: '' });
  const [team, setTeam] = useState(null);
  const idemRef = useRef(localStorage.getItem(IDEM_KEY) || makeIdemKey());

  useEffect(() => {
    if (access.loading) return;
    if (mode !== 'loading') return;
    if (searchParams.get('activated') === '1' && access.hasAccess) {
      setMode('welcome');
      return;
    }
    setMode(access.hasAccess ? 'welcome' : 'offer');
  }, [access.loading, access.hasAccess, searchParams, mode]);

  const setField = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const handleStartTrial = async () => {
    setLoading(true);
    try {
      const res = await createAccountCheckout(window.location.origin);
      window.location.href = res.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || t('self_onboarding:offer.error'));
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!form.club.trim() || !form.team.trim()) return;

    setLoading(true);

    try {
      const idem = idemRef.current;
      localStorage.setItem(IDEM_KEY, idem);

      const res = await provisionSelfServiceTeam({
        club_name: form.club.trim(),
        team_name: form.team.trim(),
        age_group: form.age.trim() || null,
        game_format: null,
        idempotency_key: idem,
        timezone: (() => {
          try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone;
          } catch {
            return null;
          }
        })(),
      });

      const created = res.data.team;
      setTeam(created);
      localStorage.setItem(PENDING_TEAM_KEY, created.id);
      setMode('success');
    } catch (error) {
      const detail = error.response?.data?.detail || '';

      if (detail.includes('activation_required')) {
        setMode('offer');
        toast.error(t('self_onboarding:offer.error'));
      } else if (detail.includes('team_limit_reached')) {
        toast.error(t('self_onboarding:create.limit'));
      } else {
        toast.error(t('self_onboarding:create.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const enterTeam = async (path) => {
    setLoading(true);

    try {
      if (team?.id) await switchTeam(team.id);
    } catch {
      // noop
    }

    try {
      await refreshUser();
    } catch {
      // noop
    }

    window.location.href = path;
  };

  if (mode === 'join') {
    return <Onboarding onComplete={completeOnboarding} />;
  }

  return (
    <OnboardingShell mode={mode}>
      {mode === 'loading' && (
        <Card className={SURFACE_CLASS} data-testid="onboarding-loading">
          <CardContent className="flex flex-col items-center justify-center px-6 py-14 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-[#C6A85E]/20 bg-[#C6A85E]/[0.07]">
              <Loader2 className="h-6 w-6 animate-spin text-[rgb(var(--tn-text-c6a85e))]" />
            </div>
            <p className="mt-5 text-sm text-[rgb(var(--tn-text-737373))]">
              {t('self_onboarding:flow.loading')}
            </p>
          </CardContent>
        </Card>
      )}

      {mode === 'offer' && (
        <Card className={SURFACE_CLASS} data-testid="billing-offer">
          <div className="border-b border-[rgb(var(--tn-border-ffffff))]/[0.06] px-6 pb-6 pt-7 text-center sm:px-8 sm:pb-7 sm:pt-8">
            <div className="mx-auto inline-flex items-center gap-2 rounded-full border border-[#C6A85E]/20 bg-[#C6A85E]/[0.07] px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[rgb(var(--tn-text-c6a85e))]">
              <Sparkles className="h-3 w-3" />
              {t('self_onboarding:offer.product')}
            </div>

            <h1 className="mt-5 text-[34px] font-semibold tracking-[-0.035em] text-[rgb(var(--tn-text-ffffff))] sm:text-[40px]">
              {t('self_onboarding:offer.headline')}
            </h1>

            <p className="mt-2 text-base font-semibold text-[rgb(var(--tn-text-ffffff))]">
              {t('self_onboarding:offer.today')}
            </p>

            <p className="mt-1 text-sm text-[rgb(var(--tn-text-737373))]">
              {t('self_onboarding:offer.after')}
            </p>
          </div>

          <CardContent className="px-6 py-6 sm:px-8 sm:py-7">
            <div className="grid grid-cols-2 gap-2.5">
              <div className="rounded-2xl border border-[rgb(var(--tn-border-ffffff))]/[0.06] bg-[rgb(var(--tn-tint-ffffff))]/[0.02] p-3.5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[rgb(var(--tn-text-525252))]">
                  {t('self_onboarding:offer.summary_trial_label')}
                </p>
                <p className="mt-1.5 text-sm font-semibold text-[rgb(var(--tn-text-ffffff))]">
                  {t('self_onboarding:offer.summary_trial_value')}
                </p>
              </div>

              <div className="rounded-2xl border border-[rgb(var(--tn-border-ffffff))]/[0.06] bg-[rgb(var(--tn-tint-ffffff))]/[0.02] p-3.5">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[rgb(var(--tn-text-525252))]">
                  {t('self_onboarding:offer.summary_today_label')}
                </p>
                <p className="mt-1.5 text-sm font-semibold text-[rgb(var(--tn-text-ffffff))]">
                  {t('self_onboarding:offer.today')}
                </p>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              {[
                'point_full_access',
                'point_no_charge',
                'point_pm',
                'point_auto',
                'point_cancel',
              ].map((key) => (
                <div key={key} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#C6A85E]/10">
                    <Check className="h-3.5 w-3.5 text-[rgb(var(--tn-text-c6a85e))]" />
                  </span>
                  <span className="text-[13px] leading-5 text-[rgb(var(--tn-text-d4d4d4))]">
                    {t(`self_onboarding:offer.${key}`)}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-5 flex items-start gap-3 rounded-2xl border border-[rgb(var(--tn-border-ffffff))]/[0.06] bg-[rgb(var(--tn-bg-0b0e13))]/70 p-3.5">
              <CreditCard className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(var(--tn-text-737373))]" />
              <div>
                <p className="text-xs font-medium text-[rgb(var(--tn-text-d4d4d4))]">
                  {t('self_onboarding:offer.payment_title')}
                </p>
                <p className="mt-1 text-[11.5px] leading-5 text-[rgb(var(--tn-text-525252))]">
                  {t('self_onboarding:offer.payment_body')}
                </p>
              </div>
            </div>

            <Button
              onClick={handleStartTrial}
              disabled={loading}
              className={`mt-6 w-full ${PRIMARY_BUTTON_CLASS}`}
              data-testid="start-trial-btn"
            >
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="mr-2 h-4 w-4" />
              )}
              {loading
                ? t('self_onboarding:offer.starting')
                : t('self_onboarding:offer.cta')}
            </Button>

            <div className="mt-3 flex items-center justify-center gap-2 text-[11px] text-[rgb(var(--tn-text-525252))]">
              <ShieldCheck className="h-3.5 w-3.5" />
              {t('self_onboarding:offer.first_charge_generic')}
            </div>

            <div className="mt-5 border-t border-[rgb(var(--tn-border-ffffff))]/[0.06] pt-4 text-center">
              <button
                type="button"
                onClick={() => setMode('join')}
                className="text-sm text-[rgb(var(--tn-text-a3a3a3))] transition-colors hover:text-[rgb(var(--tn-text-ffffff))]"
                data-testid="offer-join-btn"
              >
                {t('self_onboarding:offer.join_invite')}
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      {mode === 'welcome' && (
        <Card className={SURFACE_CLASS} data-testid="onboarding-welcome">
          <CardHeader className="px-6 pb-3 pt-8 text-center sm:px-8 sm:pt-9">
            <div
              className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-[#C6A85E]/25 bg-[#C6A85E]/10"
              style={{ boxShadow: '0 0 42px rgba(198,168,94,0.14)' }}
            >
              <Sparkles className="h-7 w-7 text-[rgb(var(--tn-text-c6a85e))]" />
            </div>

            <CardTitle className="mt-4 text-[28px] font-semibold tracking-[-0.025em] text-[rgb(var(--tn-text-ffffff))]">
              {t('self_onboarding:welcome.title')}
            </CardTitle>

            <CardDescription className="mx-auto max-w-sm text-[13.5px] leading-6 text-[rgb(var(--tn-text-737373))]">
              {t('self_onboarding:welcome.body')}
            </CardDescription>
          </CardHeader>

          <CardContent className="px-6 pb-8 pt-4 sm:px-8 sm:pb-9">
            <div className="mb-5 rounded-2xl border border-emerald-500/15 bg-emerald-500/[0.055] p-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[rgb(var(--tn-text-34d399))]" />
                <div>
                  <p className="text-sm font-semibold text-[rgb(var(--tn-text-ffffff))]">
                    {t('self_onboarding:welcome.activated_title')}
                  </p>
                  <p className="mt-1 text-xs leading-5 text-[rgb(var(--tn-text-737373))]">
                    {t('self_onboarding:welcome.activated_body')}
                  </p>
                </div>
              </div>
            </div>

            <Button
              onClick={() => setMode('create')}
              className={`w-full ${PRIMARY_BUTTON_CLASS}`}
              data-testid="welcome-create-team-btn"
            >
              {t('self_onboarding:welcome.cta')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      )}

      {mode === 'create' && (
        <Card className={SURFACE_CLASS} data-testid="create-team-form">
          <CardHeader className="px-6 pb-3 pt-7 sm:px-8 sm:pt-8">
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl border border-[#C6A85E]/20 bg-[#C6A85E]/[0.07]">
              <UsersRound className="h-4 w-4 text-[rgb(var(--tn-text-c6a85e))]" />
            </div>

            <CardTitle className="text-[28px] font-semibold tracking-[-0.025em] text-[rgb(var(--tn-text-ffffff))]">
              {t('self_onboarding:create.title')}
            </CardTitle>

            <CardDescription className="text-[13.5px] leading-6 text-[rgb(var(--tn-text-737373))]">
              {t('self_onboarding:create.subtitle')}
            </CardDescription>

            <p className="pt-1 text-[11px] text-[rgb(var(--tn-text-525252))]">
              {t('self_onboarding:create.time_hint')}
            </p>
          </CardHeader>

          <CardContent className="space-y-4 px-6 pb-7 pt-3 sm:px-8 sm:pb-8">
            <div className="space-y-2">
              <div className="flex items-center gap-2.5">
                <FieldIcon>
                  <Building2 className="h-4 w-4" />
                </FieldIcon>
                <Label htmlFor="club" className="text-[13px] text-[rgb(var(--tn-text-d4d4d4))]">
                  {t('self_onboarding:create.club_label')}
                </Label>
              </div>
              <Input
                id="club"
                value={form.club}
                onChange={setField('club')}
                placeholder={t('self_onboarding:create.club_placeholder')}
                data-testid="input-club"
                className={INPUT_CLASS}
                autoComplete="organization"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2.5">
                <FieldIcon>
                  <Users className="h-4 w-4" />
                </FieldIcon>
                <Label htmlFor="team" className="text-[13px] text-[rgb(var(--tn-text-d4d4d4))]">
                  {t('self_onboarding:create.team_label')}
                </Label>
              </div>
              <Input
                id="team"
                value={form.team}
                onChange={setField('team')}
                placeholder={t('self_onboarding:create.team_placeholder')}
                data-testid="input-team"
                className={INPUT_CLASS}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2.5">
                <FieldIcon>
                  <CalendarRange className="h-4 w-4" />
                </FieldIcon>
                <div className="flex min-w-0 items-baseline gap-2">
                  <Label htmlFor="age" className="text-[13px] text-[rgb(var(--tn-text-d4d4d4))]">
                    {t('self_onboarding:create.age_label')}
                  </Label>
                  <span className="text-[11px] text-[rgb(var(--tn-text-525252))]">
                    {t('self_onboarding:create.age_optional')}
                  </span>
                </div>
              </div>
              <Input
                id="age"
                value={form.age}
                onChange={setField('age')}
                placeholder={t('self_onboarding:create.age_placeholder')}
                data-testid="input-age"
                className={INPUT_CLASS}
              />
            </div>

            <div className="flex flex-col-reverse gap-2 pt-3 sm:flex-row">
              <Button
                variant="outline"
                onClick={() => setMode('welcome')}
                className="h-12 flex-1 rounded-xl border-[rgb(var(--tn-border-ffffff))]/10 bg-[rgb(var(--tn-tint-ffffff))]/[0.02] text-[rgb(var(--tn-text-d4d4d4))] hover:bg-[rgb(var(--tn-tint-ffffff))]/[0.05] hover:text-[rgb(var(--tn-text-ffffff))]"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('self_onboarding:common.back')}
              </Button>

              <Button
                onClick={handleCreateTeam}
                disabled={loading || !form.club.trim() || !form.team.trim()}
                className={`flex-1 ${PRIMARY_BUTTON_CLASS}`}
                data-testid="create-team-submit"
              >
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <ArrowRight className="mr-2 h-4 w-4" />
                )}
                {loading
                  ? t('self_onboarding:create.creating')
                  : t('self_onboarding:create.submit')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {mode === 'success' && (
        <Card className={SURFACE_CLASS} data-testid="onboarding-success">
          <CardHeader className="px-6 pb-3 pt-8 text-center sm:px-8 sm:pt-9">
            <div
              className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.08]"
              style={{ boxShadow: '0 0 42px rgba(52,211,153,0.10)' }}
            >
              <CheckCircle2 className="h-8 w-8 text-[rgb(var(--tn-text-34d399))]" />
            </div>

            <CardTitle className="mt-4 text-[28px] font-semibold tracking-[-0.025em] text-[rgb(var(--tn-text-ffffff))]">
              {t('self_onboarding:success.title')}
            </CardTitle>

            <CardDescription className="mx-auto max-w-sm text-[13.5px] leading-6 text-[rgb(var(--tn-text-737373))]">
              {t('self_onboarding:success.subtitle')}
            </CardDescription>
          </CardHeader>

          <CardContent className="px-6 pb-8 pt-4 sm:px-8 sm:pb-9">
            <div className="mb-5 rounded-2xl border border-[rgb(var(--tn-border-ffffff))]/[0.06] bg-[rgb(var(--tn-tint-ffffff))]/[0.02] p-4 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[rgb(var(--tn-text-525252))]">
                {t('self_onboarding:success.team_label')}
              </p>
              <p className="mt-1.5 text-base font-semibold text-[rgb(var(--tn-text-ffffff))]">
                {form.team.trim()}
              </p>
              <p className="mt-0.5 text-xs text-[rgb(var(--tn-text-737373))]">
                {form.club.trim()}
              </p>
            </div>

            <Button
              onClick={() => enterTeam(team ? `/team/${team.id}/onboarding` : '/oversikt')}
              disabled={loading}
              className={`w-full ${PRIMARY_BUTTON_CLASS}`}
              data-testid="success-invite-btn"
            >
              {loading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Users className="mr-2 h-4 w-4" />
              )}
              {t('self_onboarding:success.invite_players')}
            </Button>

            <Button
              variant="outline"
              onClick={() => enterTeam('/calendar')}
              disabled={loading}
              className="mt-2.5 h-12 w-full rounded-xl border-[rgb(var(--tn-border-ffffff))]/10 bg-[rgb(var(--tn-tint-ffffff))]/[0.02] text-[rgb(var(--tn-text-d4d4d4))] hover:bg-[rgb(var(--tn-tint-ffffff))]/[0.05] hover:text-[rgb(var(--tn-text-ffffff))]"
              data-testid="success-plan-btn"
            >
              <CalendarPlus className="mr-2 h-4 w-4" />
              {t('self_onboarding:success.plan_training')}
            </Button>

            <p className="mt-4 text-center text-[11.5px] leading-5 text-[rgb(var(--tn-text-525252))]">
              {t('self_onboarding:success.next_hint')}
            </p>
          </CardContent>
        </Card>
      )}
    </OnboardingShell>
  );
}
